/**
 * Admin Service
 * 
 * Administrative functions for managing families and users.
 * Adapted from the main app's adminService for web use.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp,
  deleteDoc,
  setDoc,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, firestore } from './firebase/config';

export interface Family {
  id: string;
  name: string;
  createdBy: string;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
  stripeCustomerId?: string | { id?: string; [key: string]: any }; // Can be string ID or full Stripe customer object
  subscriptionStatus?: string;
  subscriptionTier?: string;
  subscriptionId?: string;
  maxUsers?: number;
  maxCaregivers?: number;
  maxFamilyMembers?: number;
  // Disabled reason fields
  disabledReason?: string; // Reason code (e.g., "payment_failed")
  disabledReasonTitle?: string; // Custom title
  disabledReasonMessage?: string; // Custom message
  disabledAt?: any; // Timestamp when disabled
  [key: string]: any; // Allow additional fields from Firestore
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'user' | 'familyMember';
  familyId?: string;
  isActive?: boolean; // User active status (inherits from family when family is disabled)
  createdAt: any;
  updatedAt: any;
  photoURL?: string;
}

/**
 * Get all families with statistics
 */
export const getFamiliesWithStats = async (): Promise<(Family & { userCount: number; caregiverCount: number; memberCount: number })[]> => {
  const familiesSnapshot = await getDocs(collection(firestore, 'families'));
  
  // Normalize families data - extract stripeCustomerId if it's an object
  const families = familiesSnapshot.docs.map(doc => {
    const data = doc.data();
    let stripeCustomerId: string | undefined;
    if (data.stripeCustomerId) {
      if (typeof data.stripeCustomerId === 'string') {
        stripeCustomerId = data.stripeCustomerId;
      } else if (typeof data.stripeCustomerId === 'object' && data.stripeCustomerId.id) {
        stripeCustomerId = data.stripeCustomerId.id;
      }
    }
    
    return {
      id: doc.id,
      ...data,
      stripeCustomerId, // Use normalized value
    } as Family;
  });

  const familiesWithStats = await Promise.all(
    families.map(async (family) => {
      const usersSnapshot = await getDocs(
        query(collection(firestore, 'users'), where('familyId', '==', family.id))
      );
      const users = usersSnapshot.docs.map(doc => doc.data()) as User[];
      
      return {
        ...family,
        userCount: users.length,
        caregiverCount: users.filter(u => u.role === 'user').length,
        memberCount: users.filter(u => u.role === 'familyMember').length,
      };
    })
  );

  return familiesWithStats;
};

/**
 * Get a single family by ID
 */
export const getFamily = async (familyId: string): Promise<Family | null> => {
  const familyDoc = await getDoc(doc(firestore, 'families', familyId));
  if (!familyDoc.exists()) {
    return null;
  }
  const data = familyDoc.data();
  
  // Normalize stripeCustomerId - extract ID if it's an object
  let stripeCustomerId: string | undefined;
  if (data.stripeCustomerId) {
    if (typeof data.stripeCustomerId === 'string') {
      stripeCustomerId = data.stripeCustomerId;
    } else if (typeof data.stripeCustomerId === 'object' && data.stripeCustomerId.id) {
      stripeCustomerId = data.stripeCustomerId.id;
    }
  }
  
  return {
    id: familyDoc.id,
    ...data,
    stripeCustomerId, // Use normalized value
  } as Family;
};

/**
 * Create a new family
 */
export const createFamily = async (name: string, adminId: string): Promise<string> => {
  const familyRef = await addDoc(collection(firestore, 'families'), {
    name,
    createdBy: adminId,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return familyRef.id;
};

/**
 * Update a family
 * When disabling a family, also disables all users in that family
 * When reactivating a family, also reactivates all users in that family
 */
export const updateFamily = async (familyId: string, updates: Partial<Family>): Promise<void> => {
  const updateData: any = {
    ...updates,
    updatedAt: serverTimestamp(),
  };

  // If deactivating, set disabledAt timestamp
  if (updates.isActive === false && !updates.disabledAt) {
    updateData.disabledAt = serverTimestamp();
  }

  // If reactivating, clear disabled reason fields
  if (updates.isActive === true) {
    updateData.disabledReason = null;
    updateData.disabledReasonTitle = null;
    updateData.disabledReasonMessage = null;
    updateData.disabledAt = null;
  }

  // Update the family document
  await updateDoc(doc(firestore, 'families', familyId), updateData);

  // If disabling or reactivating, update all users in the family
  if (updates.isActive !== undefined) {
    try {
      const usersSnapshot = await getDocs(
        query(collection(firestore, 'users'), where('familyId', '==', familyId))
      );

      if (usersSnapshot.empty) {
        console.log(`No users found for family ${familyId}`);
        return;
      }

      // Batch update all users in the family
      const userUpdates = usersSnapshot.docs.map(userDoc =>
        updateDoc(doc(firestore, 'users', userDoc.id), {
          isActive: updates.isActive,
          updatedAt: serverTimestamp(),
        })
      );

      await Promise.all(userUpdates);
      console.log(`Updated ${usersSnapshot.docs.length} user(s) to isActive=${updates.isActive} for family ${familyId}`);
    } catch (error) {
      console.error(`Error updating users for family ${familyId}:`, error);
      // Don't throw - family update succeeded, user updates are secondary
      // Log the error but don't fail the entire operation
    }
  }
};

/**
 * Get all users in a family
 */
export const getFamilyUsers = async (familyId: string): Promise<User[]> => {
  const usersSnapshot = await getDocs(
    query(collection(firestore, 'users'), where('familyId', '==', familyId))
  );
  return usersSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as User[];
};

/**
 * Get all users (admin only)
 */
export const getAllUsers = async (): Promise<User[]> => {
  const usersSnapshot = await getDocs(collection(firestore, 'users'));
  return usersSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as User[];
};

/**
 * Add a user to a family
 * User's isActive status will match the family's isActive status
 */
export const addUserToFamily = async (
  familyId: string,
  email: string,
  displayName: string,
  role: 'user' | 'familyMember',
  password: string
): Promise<string> => {
  // Get family to check if it's active
  const familyDoc = await getDoc(doc(firestore, 'families', familyId));
  const familyData = familyDoc.data();
  const familyIsActive = familyData?.isActive !== false; // Default to true if not set

  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const firebaseUser = userCredential.user;
  
  await setDoc(doc(firestore, 'users', firebaseUser.uid), {
    email,
    displayName,
    role,
    familyId,
    isActive: familyIsActive, // Match family's active status
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  
  return firebaseUser.uid;
};

/**
 * Update a user
 */
export const updateUser = async (userId: string, updates: Partial<User>): Promise<void> => {
  await updateDoc(doc(firestore, 'users', userId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete a user
 */
export const deleteUser = async (userId: string): Promise<void> => {
  await deleteDoc(doc(firestore, 'users', userId));
};

/**
 * Verify if a user is an admin
 */
export const isAdmin = async (userId: string): Promise<boolean> => {
  const userDoc = await getDoc(doc(firestore, 'users', userId));
  if (!userDoc.exists()) {
    return false;
  }
  const userData = userDoc.data();
  return userData.role === 'admin';
};

